import { useState, useEffect } from 'react';
import { Move, GymnastFriend, UserProgress } from '../../types';
import GymnastAvatar from '../GymnastAvatar';
import { Star, ChevronLeft, ChevronRight, CheckCircle2, ShieldAlert, Timer, RotateCcw } from 'lucide-react';
import { fireFullScreenStepConfetti, fireMasteryConfetti } from '../../utils/confetti';

interface VariantProps {
  moves: Move[];
  friends: GymnastFriend[];
  progress: UserProgress;
  onToggleStep: (stepId: string, move: Move) => { nowCompleted: boolean; moveJustMastered: boolean };
  onSelectFriend: (friendId: string) => void;
  onResetProgress: () => void;
}

export default function VariantA({
  moves,
  friends,
  progress,
  onToggleStep,
  onSelectFriend,
  onResetProgress,
}: VariantProps) {
  const [selectedMoveId, setSelectedMoveId] = useState<string>(moves[0]?.id || 'move-backbend');
  const [currentStepIndex, setCurrentStepIndex] = useState<number>(0);
  const [timerSeconds, setTimerSeconds] = useState<number | null>(null);
  const [timerRunning, setTimerRunning] = useState<boolean>(false);

  const activeMove = moves.find(m => m.id === selectedMoveId) || moves[0];
  const activeFriend = friends.find(f => f.id === activeMove.coachFriendId) || friends[0];
  const steps = activeMove.steps;
  const currentStep = steps[currentStepIndex] || steps[0];
  const isStepComplete = !!progress.completedStepIds[currentStep.id];
  const isMoveMastered = !!progress.masteredMoveIds[activeMove.id];

  // Rotating cheer line from friend
  const [cheerIndex, setCheerIndex] = useState<number>(0);

  const rotateCheer = () => {
    setCheerIndex(prev => (prev + 1) % activeFriend.cheers.length);
  };

  useEffect(() => {
    setCheerIndex(prev => (prev + 1) % activeFriend.cheers.length);
  }, [currentStepIndex, activeFriend]);

  const currentCheer = activeFriend.cheers[cheerIndex % activeFriend.cheers.length] || activeFriend.tagline;

  const [celebrating, setCelebrating] = useState<boolean>(false);

  // Hold Timer logic
  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null;
    if (timerRunning && timerSeconds !== null && timerSeconds > 0) {
      interval = setInterval(() => {
        setTimerSeconds(s => (s !== null && s > 1 ? s - 1 : 0));
      }, 1000);
    } else if (timerSeconds === 0 && timerRunning) {
      setTimerRunning(false);
      setCelebrating(true);
      fireFullScreenStepConfetti(() => {
        setCelebrating(false);
      });
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [timerRunning, timerSeconds]);

  const handleStepToggle = () => {
    const res = onToggleStep(currentStep.id, activeMove);
    if (res.moveJustMastered) {
      setCelebrating(true);
      fireMasteryConfetti(() => {
        setCelebrating(false);
      });
    } else if (res.nowCompleted) {
      setCelebrating(true);
      fireFullScreenStepConfetti(() => {
        setCelebrating(false);
        // Automatically advance to the next step when confetti finishes!
        setCurrentStepIndex(prev => (prev < steps.length - 1 ? prev + 1 : prev));
      });
    }
  };

  const startHoldTimer = (seconds: number) => {
    setTimerSeconds(seconds);
    setTimerRunning(true);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 pb-24">
      {/* Floating Celebration Toast */}
      {celebrating && (
        <div className="fixed top-8 left-1/2 -translate-x-1/2 z-50 animate-bounce pointer-events-none">
          <div className="bg-gradient-to-r from-yellow-300 via-pink-400 to-teal-300 text-slate-900 font-black px-8 py-3.5 rounded-full shadow-2xl border-4 border-white flex items-center gap-3 text-lg md:text-2xl">
            <span className="text-3xl">🎉</span>
            <span className="drop-shadow-sm">SUPERSTAR EMMA! ⭐</span>
            <span className="text-3xl">✨</span>
          </div>
        </div>
      )}

      {/* Top Bar */}
      <header className="flex items-center justify-between bg-white/90 backdrop-blur-md rounded-3xl p-4 md:p-6 shadow-md border-2 border-pink-200 mb-6">
        <div className="flex items-center gap-3">
          <div className="text-3xl">🤸‍♀️</div>
          <div>
            <h1 className="text-xl md:text-2xl font-black text-pink-600">
              Emma's Gymnastics Club
            </h1>
            <p className="text-xs md:text-sm font-semibold text-purple-600">
              Coached by the Gymnast Friends!
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 bg-yellow-100 border-2 border-yellow-300 px-3.5 py-1.5 rounded-full shadow-inner">
            <Star className="w-6 h-6 text-yellow-500 fill-yellow-400 animate-pulse" />
            <span className="text-lg font-black text-yellow-800">{progress.earnedStars}</span>
            <span className="text-xs font-bold text-yellow-700 hidden sm:inline">Stars</span>
          </div>

          <button
            onClick={onResetProgress}
            title="Reset progress"
            className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>
      </header>

      {/* Move Selector Pills */}
      <nav aria-label="Gymnastics moves" className="flex items-center gap-2 overflow-x-auto pb-3 mb-6 scrollbar-none">
        {moves.map(m => {
          const isSelected = m.id === activeMove.id;
          const isMastered = !!progress.masteredMoveIds[m.id];
          return (
            <button
              key={m.id}
              onClick={() => {
                setSelectedMoveId(m.id);
                setCurrentStepIndex(0);
                onSelectFriend(m.coachFriendId);
              }}
              className={`flex items-center gap-2 px-4 py-2 rounded-2xl font-bold text-sm whitespace-nowrap transition-all shadow-sm ${
                isSelected
                  ? 'bg-pink-500 text-white shadow-pink-200 scale-105 ring-2 ring-pink-300'
                  : 'bg-white/80 text-slate-700 hover:bg-white hover:text-pink-600'
              }`}
            >
              <span>{m.badgeEmoji}</span>
              <span>{m.name}</span>
              {isMastered && <span className="text-yellow-300 font-extrabold text-xs">★ Mastered</span>}
            </button>
          );
        })}
      </nav>

      {/* Hero Card Stage */}
      <main className="bg-white rounded-3xl shadow-xl border-4 border-pink-200 p-6 md:p-8 relative overflow-hidden">
        {/* Step Indicator Header */}
        <div className="flex items-center justify-between border-b border-pink-100 pb-4 mb-6">
          <div className="flex items-center gap-2">
            <span className="bg-pink-500 text-white text-xs md:text-sm font-black px-3 py-1 rounded-full uppercase tracking-wider">
              Step {currentStepIndex + 1} of {steps.length}
            </span>
            <h2 className="text-lg md:text-xl font-bold text-slate-800">
              {currentStep.title}
            </h2>
            {isMoveMastered && (
              <span className="text-xs font-black bg-yellow-400 text-yellow-900 px-2.5 py-0.5 rounded-full shadow-sm">
                ★ MASTERED
              </span>
            )}
          </div>

          {/* Mini step dots */}
          <div className="flex items-center gap-1.5">
            {steps.map((s, idx) => (
              <button
                key={s.id}
                onClick={() => setCurrentStepIndex(idx)}
                className={`w-3.5 h-3.5 rounded-full transition-all ${
                  idx === currentStepIndex
                    ? 'bg-pink-500 scale-125 ring-2 ring-pink-200'
                    : progress.completedStepIds[s.id]
                    ? 'bg-yellow-400'
                    : 'bg-slate-200 hover:bg-slate-300'
                }`}
                title={`Jump to step ${idx + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Content Body Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          {/* Main Instructions (2 cols) */}
          <div className="lg:col-span-2 space-y-4">
            <p className="text-base md:text-lg text-slate-700 font-medium leading-relaxed">
              {currentStep.description}
            </p>

            <div className="bg-pink-50/70 rounded-2xl p-4 border border-pink-200">
              <h3 className="text-xs uppercase font-extrabold text-pink-600 tracking-wider mb-2">
                What to do:
              </h3>
              <ol className="space-y-2">
                {currentStep.instructions.map((inst, i) => (
                  <li key={i} className="flex items-start gap-3 text-slate-700 font-medium">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-pink-200 text-pink-700 font-bold text-xs flex items-center justify-center mt-0.5">
                      {i + 1}
                    </span>
                    <span>{inst}</span>
                  </li>
                ))}
              </ol>
            </div>

            {currentStep.safetyTip && (
              <div className="flex items-start gap-3 bg-amber-50 rounded-2xl p-4 border border-amber-200 text-amber-900 text-sm">
                <ShieldAlert className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="font-bold">Safety First: </span>
                  <span>{currentStep.safetyTip}</span>
                </div>
              </div>
            )}

            {/* Optional Hold Timer */}
            {currentStep.holdSeconds && (
              <div className="flex items-center gap-4 bg-purple-50 rounded-2xl p-4 border border-purple-200">
                <Timer className="w-6 h-6 text-purple-600" />
                <div className="flex-1">
                  <div className="text-xs uppercase font-bold text-purple-700">Practice Hold Timer</div>
                  <div className="text-xl font-black text-purple-900">
                    {timerSeconds !== null ? `${timerSeconds}s` : `${currentStep.holdSeconds}s`}
                  </div>
                </div>
                <button
                  onClick={() => startHoldTimer(currentStep.holdSeconds || 10)}
                  disabled={timerRunning}
                  className={`px-4 py-2 rounded-xl font-bold text-sm text-white shadow transition-all ${
                    timerRunning
                      ? 'bg-purple-300 cursor-not-allowed'
                      : 'bg-purple-600 hover:bg-purple-700 active:scale-95'
                  }`}
                >
                  {timerRunning ? 'Holding... 🧘' : 'Start Timer ⏱️'}
                </button>
              </div>
            )}
          </div>

          {/* Coach Buddy Card (1 col) */}
          <div className="bg-gradient-to-b from-purple-50 to-pink-50 rounded-2xl p-4 border-2 border-purple-200 flex flex-col items-center text-center">
            <GymnastAvatar friend={activeFriend} size="md" pose={isStepComplete ? 'cheer' : 'ready'} />
            
            <span className="mt-2 text-xs font-bold uppercase tracking-wider text-purple-600">
              Practice Buddy
            </span>
            <h4 className="text-base font-extrabold text-slate-800">
              {activeFriend.name}
            </h4>
            <span className="text-xs text-purple-700 font-medium mb-3">
              {activeFriend.title}
            </span>

            <div
              onClick={rotateCheer}
              className="relative bg-white p-3 rounded-2xl shadow-sm border border-purple-200 text-xs text-slate-700 font-medium leading-normal w-full cursor-pointer hover:border-pink-300 transition-all active:scale-95 group"
              title="Click to hear another cheer!"
            >
              <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-3 h-3 bg-white border-t border-l border-purple-200 rotate-45" />
              <p className="font-semibold text-slate-800">"{currentCheer}"</p>
              <div className="text-[10px] text-pink-500 font-bold mt-1.5 flex items-center justify-center gap-1">
                <span>💬 Tap for another cheer!</span>
              </div>
            </div>
          </div>
        </div>

        {/* Action Controls */}
        <div className="mt-8 pt-6 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <button
              onClick={() => setCurrentStepIndex(i => Math.max(0, i - 1))}
              disabled={currentStepIndex === 0}
              className={`flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-4 py-3 rounded-2xl font-bold text-sm transition-all ${
                currentStepIndex === 0
                  ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200 active:scale-95'
              }`}
            >
              <ChevronLeft className="w-5 h-5" />
              <span>Previous Step</span>
            </button>

            <button
              onClick={() => setCurrentStepIndex(i => Math.min(steps.length - 1, i + 1))}
              disabled={currentStepIndex === steps.length - 1}
              className={`flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-4 py-3 rounded-2xl font-bold text-sm transition-all ${
                currentStepIndex === steps.length - 1
                  ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200 active:scale-95'
              }`}
            >
              <span>Next Step</span>
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          <button
            onClick={handleStepToggle}
            className={`w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl font-extrabold text-base transition-all shadow-lg active:scale-95 ${
              isStepComplete
                ? 'bg-emerald-500 text-white shadow-emerald-200 hover:bg-emerald-600'
                : 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-pink-200 hover:opacity-95'
            }`}
          >
            <CheckCircle2 className="w-6 h-6" />
            <span>{isStepComplete ? 'Step Completed! ⭐ (Tap to Undo)' : 'I Did This Step! ⭐'}</span>
          </button>
        </div>
      </main>
    </div>
  );
}
